import { useCallback, useEffect, useRef, useState } from "react";
import type { ArcadeRoom } from "@arcade/sdk";
import { connectRoom, getArcade } from "./arcade";
import type { GameAdapter, PlayerId } from "../games/game-core";

type TurnSnapshot = {
  state: unknown;
  players?: string[];
  turn: string | null;
  deadline: number | null;
};

export type WaitingReason = "connecting" | "opponent" | "their-turn" | null;

export interface TurnRoom<State, Move> {
  me: PlayerId | null;
  state: State | null;
  /** State to render: the live state, or a freshly-derived opening when no move has been made yet. */
  view: State | null;
  isMyTurn: boolean;
  /** True once both seats are known (opponent has joined or a state exists). */
  opponentReady: boolean;
  waiting: WaitingReason;
  deadline: number | null;
  error: string | null;
  /** Validate + apply a move locally, submit it, and finish the match if it becomes terminal. */
  submit: (move: Move) => Promise<void>;
  /** Re-fetch the authoritative snapshot over HTTP. */
  refresh: () => Promise<void>;
  /** Send a push reminder to the opponent when it is their turn. */
  nudge: () => Promise<void>;
}

/**
 * Drives one Arcade turn room for a game adapter: HTTP snapshot on mount, live opponent moves
 * over WebSocket, and own moves committed over HTTP. State is opaque to Arcade, so every update
 * is schema-validated and stale (lower-revision) snapshots are ignored.
 * See docs/OTHELLO_GAME_SPEC.md §7 and the turn-based developer guide.
 */
export function useTurnRoom<State, Move>(
  adapter: GameAdapter<State, Move>,
  gameId: string,
  roomId: string,
  token: string,
  displayName?: string,
): TurnRoom<State, Move> {
  const pendingOpponentId = `pending:${roomId}`;
  const [me, setMe] = useState<PlayerId | null>(null);
  const [state, setState] = useState<State | null>(null);
  const [turn, setTurn] = useState<PlayerId | null>(null);
  const [opponentId, setOpponentId] = useState<PlayerId | null>(null);
  const [seatPlayers, setSeatPlayers] = useState<[PlayerId, PlayerId] | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);
  const [nudging, setNudging] = useState(false);

  const roomRef = useRef<ArcadeRoom | null>(null);
  const stateRef = useRef<State | null>(null);
  const meRef = useRef<PlayerId | null>(null);
  // Cache the pre-first-move opening per player-pair. Games with a seeded deal must not
  // re-derive (reshuffle) on every render, and `view` and `submit` must share one base.
  const bootstrapRef = useRef<{ key: string; state: State } | null>(null);
  stateRef.current = state;
  meRef.current = me;

  const bootstrapFor = useCallback(
    (pair: [PlayerId, PlayerId]): State => {
      const key = pair.join("|");
      if (bootstrapRef.current?.key !== key) {
        bootstrapRef.current = { key, state: adapter.createInitialState(pair, undefined, roomId) };
      }
      return bootstrapRef.current.state;
    },
    [adapter, roomId],
  );

  // Apply an incoming snapshot, ignoring nulls (no moves yet) and stale revisions.
  const applyIncoming = useCallback(
    (raw: unknown, incomingTurn: string | null | undefined, incomingPlayers?: string[]) => {
      if (incomingPlayers?.length === 2 && incomingPlayers.every((player) => typeof player === "string")) {
        const pair = incomingPlayers as [PlayerId, PlayerId];
        setSeatPlayers(pair);
        const other = pair.find((player) => player !== meRef.current && player !== pendingOpponentId);
        if (other) setOpponentId(other);
      }
      if (incomingTurn !== undefined) setTurn(incomingTurn);
      if (raw == null) return;
      let validated: State;
      try {
        validated = adapter.validateState(raw);
      } catch {
        setError("The game state could not be read. Refreshing…");
        return;
      }
      const current = stateRef.current;
      const sameRevisionWithNewSeats = current
        && adapter.revision(validated) === adapter.revision(current)
        && adapter.players(validated).join("|") !== adapter.players(current).join("|");
      if (current && adapter.revision(validated) <= adapter.revision(current) && !sameRevisionWithNewSeats) return;
      stateRef.current = validated;
      setState(validated);
      if (incomingTurn === undefined) setTurn(adapter.activePlayer(validated));
      const others = adapter.players(validated).filter((p) => p !== meRef.current);
      if (others[0]) setOpponentId(others[0]);
    },
    [adapter, pendingOpponentId],
  );

  const refresh = useCallback(async () => {
    const arcade = await getArcade(gameId, displayName);
    const snap = (await arcade.getTurnState(roomId, token)) as TurnSnapshot;
    setDeadline(snap.deadline ?? null);
    applyIncoming(snap.state, snap.turn, snap.players);
  }, [applyIncoming, displayName, gameId, roomId, token]);

  useEffect(() => {
    let disposed = false;
    let room: ArcadeRoom | null = null;
    const unsubscribers: Array<() => void> = [];

    (async () => {
      try {
        const arcade = await getArcade(gameId, displayName);
        if (disposed) return;
        setMe(arcade.me);
        meRef.current = arcade.me;

        room = await connectRoom(gameId, roomId, token);
        if (disposed) {
          room.close();
          return;
        }
        roomRef.current = room;

        unsubscribers.push(
          room.on("state", (_from, incoming, message) => {
            const stateMessage = message as { turn?: string | null; players?: string[]; deadline?: number | null } | undefined;
            applyIncoming(incoming, stateMessage?.turn, stateMessage?.players);
            const dl = stateMessage?.deadline;
            if (dl !== undefined) setDeadline(dl ?? null);
          }),
          room.on("message", (message: Record<string, unknown>) => {
            if (message.t !== "welcome") return;
            if (typeof message.turn === "string" || message.turn === null) setTurn(message.turn as string | null);
            if (typeof message.deadline === "number") setDeadline(message.deadline);
            if (Array.isArray(message.players) && message.players.length === 2 && message.players.every((player) => typeof player === "string")) {
              setSeatPlayers(message.players as [PlayerId, PlayerId]);
            }
            const peers = message.peers;
            if (Array.isArray(peers) && typeof peers[0] === "string") setOpponentId(peers[0]);
            // Re-sync the authoritative snapshot on every (re)connect. The socket only pushes state on
            // *new* moves, so without this a client that connects after the opponent already moved
            // (e.g. opened from a turn notification) would keep whatever it loaded and never update.
            void refresh();
          }),
          room.on("player_joined", (message: { playerId?: string }) => {
            if (message.playerId && message.playerId !== meRef.current) {
              setOpponentId(message.playerId);
              const current = stateRef.current;
              if (current && adapter.players(current).includes(pendingOpponentId)) {
                stateRef.current = null;
                setState(null);
              }
              void refresh();
            }
          }),
          room.on("over", () => void refresh()),
          room.on("timeout", () => void refresh()),
          room.on("rejected", (message: { reason?: string }) => {
            if (message.reason === "not_your_turn") void refresh();
          }),
        );

        await refresh();
      } catch (cause) {
        if (!disposed) setError(cause instanceof Error ? cause.message : "Could not connect to the game.");
      } finally {
        if (!disposed) setConnecting(false);
      }
    })();

    return () => {
      disposed = true;
      for (const off of unsubscribers) off();
      room?.close();
      roomRef.current = null;
    };
  }, [adapter, applyIncoming, displayName, gameId, pendingOpponentId, refresh, roomId, token]);

  // Re-fetch authoritative state whenever the app is brought back to the foreground. Turn
  // notifications resume an installed PWA without necessarily remounting this hook, so the
  // one-shot mount refresh can leave stale "waiting" state on screen until a manual reload.
  useEffect(() => {
    const resync = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    return () => {
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, [refresh]);

  // [dark, light] once resolvable: from state if present, else from turn owner (seat 0) + opponent.
  const resolvePlayers = useCallback((): [PlayerId, PlayerId] | null => {
    if (seatPlayers) return seatPlayers;
    const current = stateRef.current;
    if (current) return adapter.players(current);
    const self = meRef.current;
    if (!self) return null;
    if (turn && turn !== self) return [turn, self]; // opponent (seat 0) is dark, I'm light
    if (opponentId) return [self, opponentId]; //       I'm seat 0 (dark), opponent is light
    return [self, pendingOpponentId]; // host may take the opening move before seat 1 joins
  }, [adapter, opponentId, pendingOpponentId, seatPlayers, turn]);

  const submit = useCallback(
    async (move: Move) => {
      setError(null);
      const self = meRef.current;
      const players = resolvePlayers();
      if (!self || !players) {
        setError("Still connecting to the game. Try again in a moment.");
        return;
      }
      const base = stateRef.current ?? bootstrapFor(players);
      try {
        adapter.validateMove(base, move, self);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "That move is not legal.");
        return;
      }
      const next = adapter.reduce(base, move);
      try {
        const arcade = await getArcade(gameId, displayName);
        const result = (await arcade.submitMove(roomId, token, move, next)) as TurnSnapshot;
        applyIncoming(result.state ?? next, result.turn ?? adapter.activePlayer(next));
        if (adapter.isTerminal(next)) {
          const results = adapter
            .players(next)
            .map((pid) => ({ playerId: pid, score: adapter.summarize(next, pid).myScore }))
            .sort((a, b) => b.score - a.score);
          roomRef.current?.send({ t: "over", results });
        }
      } catch (cause) {
        const code = (cause as { code?: string; status?: number }).code;
        if (code === "not_your_turn" || (cause as { status?: number }).status === 409) {
          await refresh();
          setError("The game moved on. Here’s the latest.");
        } else {
          setError(cause instanceof Error ? cause.message : "Your move could not be submitted.");
        }
      }
    },
    [adapter, applyIncoming, bootstrapFor, displayName, gameId, refresh, resolvePlayers, roomId, token],
  );

  const players = resolvePlayers();
  const view = state ?? (players ? bootstrapFor(players) : null);
  // Before the first move Arcade can still report a null/stale turn after the second player
  // joins. The deterministic opening is authoritative here: players[0] is the host and always
  // moves first. Using only `turn` left the host and guest both waiting for the other to act.
  const isMyTurn = me != null && (view ? adapter.activePlayer(view) === me : turn === me);
  const opponentReady = players != null
    && !players.includes(pendingOpponentId)
    && (opponentId != null || players.some((player) => player !== me));
  const waiting: WaitingReason = connecting
    ? "connecting"
    : !opponentReady
      ? "opponent"
      : !isMyTurn && !(state && adapter.isTerminal(state))
        ? "their-turn"
        : null;

  const nudge = async () => {
    if (nudging) return;
    setError(null);
    setNudging(true);
    try {
      const arcade = await getArcade(gameId, displayName);
      await arcade.nudgeOpponent(roomId, token);
    } catch (cause) {
      const code = (cause as { code?: string }).code;
      setError(code === "rate_limited"
        ? "You already sent a reminder recently."
        : cause instanceof Error ? cause.message : "The reminder could not be sent.");
      throw cause;
    } finally {
      setNudging(false);
    }
  };

  return { me, state, view, isMyTurn, opponentReady, waiting, deadline, error, submit, refresh, nudge };
}
